import { createNewChat, readChatId } from "@Api/createNewChat";
import { sessionUser } from "@Auth/officeSession";
import { getTokenManager, LEGACY_USER_KEY, SIGNED_IN_KEY } from "@Auth/session";
import Bugsnag from "@bugsnag/js";
import { AccessRequired } from "@Components/AccessRequired";
import { AppBar } from "@Components/AppBar";
import { PromptLibrary } from "@Components/PromptLibrary";
import { Workflows } from "@Components/Workflows";
import { getAuthConfig } from "@Config/auth";
import { env } from "@Config/env";
import { ErrorContext, useErrorContext } from "@Hooks/contexts/useErrorContext";
import { UserContext, useUserContext } from "@Hooks/contexts/useUserContext";
import { WordContext } from "@Hooks/contexts/useWordContext";
import { useTrackChangesStatus } from "@Hooks/office/useTrackChangesStatus";
import { useInitialLanguage } from "@Hooks/useInitialLanguage";
import { useJuristIQAccess } from "@Hooks/useJuristIQAccess";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { theme } from "@Styles/theme";
import { bookmarkAllHeadersAndLists } from "@Utils/doc/bookmarkAllHeadersAndLists";
import { getOrCreateDocId } from "@Utils/doc/getOrCreateDocId";
import { invalidateSectionCache } from "@Utils/doc/sectionCache";
import { reducedMotion, ShellBarContext, Toast } from "@UI";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatV2 } from "../v2/ChatV2";
import { useChatSessionStore } from "../v2/store/chatSessionStore";
import { SignIn } from "./SignIn";
const Shell = styled("div")({
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "hidden"
});
const pushIn = keyframes`from { opacity: 0; transform: translateX(24px); }`;
const fadeIn = keyframes`from { opacity: 0; }`;
const BarSlot = styled("div")({
    flexShrink: 0,
    position: "relative",
    zIndex: 2,
    "& > *": {
        animation: `${pushIn} var(--j-dur-size) var(--j-ease)`
    },
    [reducedMotion]: {
        "& > *": {
            animationName: fadeIn
        }
    }
});
const Content = styled("main")({
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
});
const Panel = styled("div")({
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    "&[hidden]": {
        display: "none"
    }
});
function App() {
    const { user, setUser } = useUserContext((s)=>s);
    const { error, setError } = useErrorContext((s)=>s);
    // Chat identity lives in the v2 session store (single source of truth read by
    // the transport + chat hooks). The shell only seeds it and gates on it.
    const chatId = useChatSessionStore((s)=>s.chatId);
    const docId = useChatSessionStore((s)=>s.docId);
    const setSession = useChatSessionStore((s)=>s.setSession);
    const token = useMemo(()=>user?.meta?.access_token.token, [
        user
    ]);
    const [isLoginInProgress, setLoginInProgress] = useState(false);
    const [authState, setAuthState] = useState(()=>getTokenManager().getState());
    const isRestoring = useRef(true);
    useTrackChangesStatus();
    // JuristIQ access control
    const { hasAccess: hasJuristIQAccess, isLoading: isAccessLoading } = useJuristIQAccess();
    const [activeTab, setActiveTab] = useState("chat");
    const [input, setInput] = useState("");
    const [barSlot, setBarSlot] = useState(null);
    const [isPushed, setPushed] = useState(false);
    const tabProps = (tab)=>isPushed ? {} : {
            role: "tabpanel",
            "aria-labelledby": `tab-${tab}`
        };
    const shellBar = useMemo(()=>({
            slot: barSlot,
            setPushed
        }), [
        barSlot
    ]);
    const seedChat = async ()=>{
        const documentId = await getOrCreateDocId();
        const existingChatId = readChatId();
        setSession({
            chatId: existingChatId ?? "",
            docId: documentId
        });
        if (existingChatId === null) {
            createNewChat();
        }
    };
    const handleLoginClick = useCallback(async ()=>{
        setLoginInProgress(true);
        try {
            const auth = await getAuthConfig();
            Office.context.ui.displayDialogAsync(auth.redirect_uri, (asyncResult)=>{
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    setLoginInProgress(false);
                    return;
                }
                const dialog = asyncResult.value;
                const listening = new AbortController();
                const complete = ()=>{
                    if (listening.signal.aborted) return;
                    listening.abort();
                    dialog.close();
                    getTokenManager().initialize().finally(()=>setLoginInProgress(false));
                };
                window.addEventListener("storage", (event)=>{
                    if (event.key === SIGNED_IN_KEY && event.newValue) complete();
                }, {
                    signal: listening.signal
                });
                dialog.addEventHandler(Office.EventType.DialogEventReceived, complete);
                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg)=>{
                    if (listening.signal.aborted || !("message" in arg)) return;
                    listening.abort();
                    dialog.close();
                    if (env.isProduction) Bugsnag.notify(new Error(`Word sign-in failed: ${arg.message}`));
                    setAuthState({
                        status: "signedOut",
                        reason: "unavailable"
                    });
                    setLoginInProgress(false);
                });
            });
        } catch  {
            setLoginInProgress(false);
        }
    }, []);
    useEffect(()=>{
        const onAuthState = async (state)=>{
            setAuthState(state);
            if (state.status !== "signedIn") {
                setUser(null);
                if (state.status === "signedOut" && state.reason === null && isRestoring.current) handleLoginClick();
                isRestoring.current = false;
                return;
            }
            isRestoring.current = false;
            setUser(sessionUser(state.session));
            try {
                await seedChat();
            } catch  {
                setError("Failed to fetch user info");
            }
        };
        localStorage.removeItem(LEGACY_USER_KEY);
        invalidateSectionCache();
        const manager = getTokenManager();
        const unsubscribe = manager.subscribe(onAuthState);
        manager.initialize();
        Office.onReady(()=>{
            // Automatically bookmark headers & lists on Word/Office ready
            bookmarkAllHeadersAndLists().catch(console.warn);
        });
        return unsubscribe;
    }, []);
    useEffect(()=>{
        const dbUser = user?.meta?.dbUser;
        if (env.isProduction && dbUser) {
            Bugsnag.setUser(`${dbUser.id}`, dbUser.email, dbUser.fullName);
        }
    }, [
        user?.meta?.dbUser.id,
        user?.meta?.dbUser.email,
        user?.meta?.dbUser.fullName
    ]);
    const onUsePrompt = useCallback((prompt)=>{
        setInput(prompt);
        setActiveTab("chat");
        if (!chatId && token) {
            createNewChat();
        }
    }, [
        chatId,
        token
    ]);
    useInitialLanguage(token);
    const clearError = useCallback(()=>setError(null), [
        setError
    ]);
    if (user && !isAccessLoading && !hasJuristIQAccess) {
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(CssBaseline, null), /*#__PURE__*/ React.createElement(AccessRequired, null));
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(CssBaseline, null), /*#__PURE__*/ React.createElement(ShellBarContext.Provider, {
        value: shellBar
    }, /*#__PURE__*/ React.createElement(Shell, null, user && !isPushed && /*#__PURE__*/ React.createElement(AppBar, {
        activeTab: activeTab,
        onTabChange: setActiveTab
    }), /*#__PURE__*/ React.createElement(BarSlot, {
        ref: setBarSlot
    }), /*#__PURE__*/ React.createElement(Content, {
        id: "content-container"
    }, !user && !error && /*#__PURE__*/ React.createElement(SignIn, {
        isSigningIn: isLoginInProgress || authState.status === "restoring",
        reason: authState.status === "signedOut" ? authState.reason : null,
        onSignIn: handleLoginClick
    }), user && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Panel, {
        id: "panel-chat",
        ...tabProps("chat"),
        hidden: activeTab !== "chat"
    }, chatId && docId && /*#__PURE__*/ React.createElement(ChatV2, {
        input: input,
        setInput: setInput
    })), activeTab === "promptlib" && /*#__PURE__*/ React.createElement(Panel, {
        id: "panel-promptlib",
        ...tabProps("promptlib")
    }, /*#__PURE__*/ React.createElement(PromptLibrary, {
        onUsePrompt: onUsePrompt
    })), activeTab === "workflows" && /*#__PURE__*/ React.createElement(Panel, {
        id: "panel-workflows",
        ...tabProps("workflows")
    }, /*#__PURE__*/ React.createElement(Workflows, null)))), /*#__PURE__*/ React.createElement(Toast, {
        open: !!error,
        message: error,
        tone: "error",
        onClose: clearError
    }))));
}
function AppWithExternalProviders() {
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const isTrackChangesEnabled = useTrackChangesStatus();
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: theme
    }, /*#__PURE__*/ React.createElement(ErrorContext.Provider, {
        value: {
            error,
            setError
        }
    }, /*#__PURE__*/ React.createElement(UserContext.Provider, {
        value: {
            user,
            setUser
        }
    }, /*#__PURE__*/ React.createElement(WordContext.Provider, {
        value: {
            isTrackChangesEnabled
        }
    }, /*#__PURE__*/ React.createElement(App, null)))));
}
export { AppWithExternalProviders as App };
